import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Brain, Send, CheckCircle2, AlertTriangle, FileSignature, Compass } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { STARTUP } from '@/constants/testIds';

// Fast 12-item Likert intake (1–5). The labels are intentionally written
// "agree" so the founder can sprint through it. The server forwards each
// answer to Claude Sonnet for the OCEAN scoring, exactly like the long-form
// version — Claude infers the dimension from the prompt phrasing.
const PSYCHO_QUESTIONS = [
  'I see myself as someone who is open to new and unconventional ideas.',
  'I see myself as someone who is reserved and prefers thinking before speaking.',
  'I see myself as someone who is thorough and follows through on commitments.',
  'I see myself as someone who handles stress well and stays composed.',
  'I see myself as someone who is generally trusting of others.',
  'I take significant risks when the upside is worth it.',
  'I delegate even high-stakes decisions when my team is capable.',
  'I welcome critical feedback even when it stings.',
  'I admit mistakes quickly and change course publicly.',
  'I balance compliance and speed deliberately, not by default.',
  'When uncertain, I gather more data before deciding.',
  'I have a clear personal vision that survives bad weeks.',
];
const LIKERT_LABELS = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function StartupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [startup, setStartup] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [psycho, setPsycho] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [tab, setTab] = useState(params.get('tab') || 'overview');

  // Honor a later URL change (e.g. user clicks a step in the journey while already here)
  useEffect(() => {
    const t = params.get('tab');
    if (t) setTab(t);
  }, [params]);

  // pitch form
  const [pTitle, setPTitle] = useState('');
  const [pSummary, setPSummary] = useState('');
  const [pFull, setPFull] = useState('');
  const [psBusy, setPsBusy] = useState(false);

  // QA
  const [qaSession, setQaSession] = useState(null);
  const [qaAnswer, setQaAnswer] = useState('');
  const [qaBusy, setQaBusy] = useState(false);

  // psycho
  const [answers, setAnswers] = useState({});
  const [psyBusy, setPsyBusy] = useState(false);

  const load = async () => {
    try {
      const [s, p, ps] = await Promise.all([
        api.get(`/startups/${id}`),
        api.get(`/startups/pitches/${id}`),
        api.get(`/startups/psycho/${id}`),
      ]);
      setStartup(s.data);
      setPitches(p.data);
      setPsycho(ps.data);
    } catch { toast.error('Could not load startup'); }
  };
  // Pull the OOH/Lean roadmap silently — it's a stub on first read, so a
  // 404 / 4xx shouldn't ever block the page.
  const loadRoadmap = async () => {
    try {
      const { data } = await api.get(`/scaling-lean/roadmap/${id}`);
      setRoadmap(data);
    } catch (err) {
      console.warn('StartupDetail: roadmap silent fetch failed', err);
    }
  };
  useEffect(() => { load(); loadRoadmap(); }, [id]);

  const submitPitch = async (e) => {
    e.preventDefault();
    if (!pTitle || !pSummary || pFull.length < 80) {
      toast.error('Title, summary, and a substantial pitch text are required.');
      return;
    }
    setPsBusy(true);
    try {
      const { data } = await api.post('/startups/pitches', { startup_id: id, title: pTitle, summary: pSummary, full_text: pFull });
      toast.success(`AI categorised as: ${data.ai_category}`);
      setPTitle(''); setPSummary(''); setPFull('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pitch failed');
    } finally { setPsBusy(false); }
  };

  const startQA = async () => {
    setQaBusy(true);
    try {
      const { data } = await api.post('/startups/qa/start', { startup_id: id });
      setQaSession(data);
    } catch (e) { toast.error('AI Q&A unavailable'); }
    finally { setQaBusy(false); }
  };

  const sendAnswer = async () => {
    if (!qaAnswer.trim()) return;
    setQaBusy(true);
    try {
      const { data } = await api.post('/startups/qa/answer', { session_id: qaSession.qa_session_id, answer: qaAnswer });
      setQaSession(data);
      setQaAnswer('');
      if (data.completed) {
        toast.success(`Interview complete · verdict: ${data.final_verdict || 'review'} · score ${data.final_score}`);
        load();
      }
    } catch { toast.error('Failed to send answer'); }
    finally { setQaBusy(false); }
  };

  const submitPsycho = async (e) => {
    e.preventDefault();
    if (Object.keys(answers).length < PSYCHO_QUESTIONS.length) {
      toast.error('Please answer all questions.');
      return;
    }
    setPsyBusy(true);
    try {
      await api.post('/startups/psycho', { startup_id: id, answers });
      toast.success('Psychometric profile created.');
      load();
    } catch { toast.error('Psychometric analysis failed'); }
    finally { setPsyBusy(false); }
  };

  if (!startup) return <div className="text-slate-500 text-sm">Loading…</div>;
  const canEdit = user?.role === 'holding_admin' || user?.user_id === startup.founder_user_id;

  return (
    <div>
      <Link to="/dashboard/startups" className="ve-btn-ghost text-xs"><ArrowLeft size={12}/> All startups</Link>

      <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">{startup.industry}</div>
          <h1 className="display text-3xl md:text-4xl mt-2">{startup.name}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{startup.tagline}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="ve-card px-4 py-2 text-xs">
            <div className="text-slate-500 uppercase tracking-[0.18em]">Status</div>
            <div className="font-medium mt-0.5 capitalize">{startup.vetting_status.replace('_',' ')}</div>
          </div>
          <div className="ve-card px-4 py-2 text-xs">
            <div className="text-slate-500 uppercase tracking-[0.18em]">AI Score</div>
            <div className="display text-lg text-[#8B3FC5]">{startup.ai_score ? Math.round(startup.ai_score) : '—'}</div>
          </div>
          <div className="ve-card px-4 py-2 text-xs">
            <div className="text-slate-500 uppercase tracking-[0.18em]">Psycho</div>
            <div className="display text-lg text-[#8B3FC5]">{startup.psychometric_score ? Math.round(startup.psychometric_score) : '—'}</div>
          </div>
          <Link
            to={`/dashboard/startups/${startup.startup_id}/roadmap`}
            data-testid="open-scaling-lean-roadmap"
            className="ve-card px-4 py-2 text-xs hover:border-[#8B3FC5] transition group"
            title="Open the Scaling Lean roadmap"
          >
            <div className="text-slate-500 uppercase tracking-[0.18em] group-hover:text-[#5E1F8B]">Lean roadmap</div>
            <div className="display text-lg text-[#8B3FC5]">{startup.ooh_score ? Math.round(startup.ooh_score) : '—'}</div>
          </Link>
        </div>
      </div>

      {/* OOH pillar progress bars — visible at a glance, animate-in on load */}
      {roadmap?.stages && (
        <div className="mt-4 ve-card p-4" data-testid="startup-ooh-pillars">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
              <Compass size={12}/> OOH pillars
            </div>
            <Link
              to={`/dashboard/startups/${startup.startup_id}/roadmap`}
              className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#5E1F8B]"
            >
              See full roadmap →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ooh-pillars-grid">
            {Object.entries(roadmap.stages)
              .filter(([, v]) => v.kind === 'pillar')
              .map(([pid, p], i) => {
                const isWeak = roadmap.weakest_pillar === pid;
                return (
                  <div
                    key={pid}
                    className={`rounded p-3 border ${isWeak ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/15' : 'border-slate-200 dark:border-white/10'}`}
                    style={{ animation: `ve-pillar-in 0.45s ease ${i * 80}ms both` }}
                    data-testid={`ooh-pillar-chip-${pid}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 truncate" title={p.label}>
                        {p.label.split(' · ')[0]}
                      </div>
                      <div className={`text-sm font-bold tabular-nums ${isWeak ? 'text-amber-600 dark:text-amber-300' : 'text-[#8B3FC5]'}`}>
                        {Math.round(p.percent)}%
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${isWeak ? 'bg-amber-400' : 'bg-[#8B3FC5]'}`}
                        style={{
                          width: `${Math.max(2, Math.min(100, p.percent))}%`,
                          transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="mt-7 flex flex-wrap gap-2">
        {[
          { id:'overview', label:'Overview', icon:FileText },
          { id:'pitch', label:'Pitch & AI Categorise', icon:FileText },
          { id:'qa', label:'AI Q&A Interview', icon:Brain },
          { id:'psycho', label:'Psychometric', icon:Brain },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm rounded-full border transition-colors ${tab===t.id?'bg-[#8B3FC5] text-white border-[#8B3FC5]':'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5] hover:text-[#8B3FC5]'}`}>
            <t.icon size={14} className="inline -mt-0.5 mr-1.5"/> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 ve-card p-6">
              <h2 className="display text-xl">Pitch summaries ({pitches.length})</h2>
              <div className="mt-4 divide-y divide-slate-100">
                {pitches.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No pitches yet. Submit one in the Pitch tab.</div>}
                {pitches.map(p => (
                  <div key={p.pitch_id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{p.title}</div>
                      <span className="text-xs px-2 py-0.5 rounded bg-[#8B3FC5]/10 text-[#8B3FC5]">{p.ai_category}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2 italic">{p.ai_summary}</p>
                    <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                      <div>
                        <div className="text-emerald-700 uppercase tracking-[0.15em] mb-1">Strengths</div>
                        <ul className="space-y-1">{p.ai_strengths?.map((s,i) => <li key={`s-${i}-${String(s).slice(0, 24)}`} className="flex gap-1.5"><CheckCircle2 size={12} className="text-emerald-600 mt-0.5 shrink-0"/>{s}</li>)}</ul>
                      </div>
                      <div>
                        <div className="text-rose-700 uppercase tracking-[0.15em] mb-1">Concerns</div>
                        <ul className="space-y-1">{p.ai_concerns?.map((s,i) => <li key={`c-${i}-${String(s).slice(0, 24)}`} className="flex gap-1.5"><AlertTriangle size={12} className="text-rose-600 mt-0.5 shrink-0"/>{s}</li>)}</ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="ve-card p-6">
              <h2 className="display text-xl">Psychometric reports</h2>
              <div className="mt-4 space-y-4">
                {psycho.length === 0 && <div className="text-sm text-slate-500">No reports yet.</div>}
                {psycho.map(p => (
                  <div key={p.report_id} className="border-l-2 border-[#8B3FC5] pl-4">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Leadership index</div>
                    <div className="display text-3xl text-[#8B3FC5]">{Math.round(p.leadership_index)}</div>
                    <p className="text-sm text-slate-600 mt-2">{p.summary}</p>
                    {p.flags?.length > 0 && (
                      <div className="mt-2 text-xs text-rose-700">⚠ {p.flags.join(' · ')}</div>
                    )}
                  </div>
                ))}
              </div>
              <Link to="/dashboard/agreements" className="ve-btn-secondary mt-6 w-full justify-center">
                <FileSignature size={14}/> Generate NDA + MOU
              </Link>
            </div>
          </div>
        )}

        {tab === 'pitch' && (
          <form onSubmit={submitPitch} className="ve-card p-6 max-w-3xl">
            <h2 className="display text-xl">Submit pitch — AI will auto-categorise</h2>
            <p className="text-sm text-slate-600 mt-2">GPT-5.2 analyses your pitch and returns a category, strengths and concerns. The result will update the AI Score.</p>
            {!canEdit && <p className="mt-4 text-sm text-amber-700">Only the founder or admin can submit pitches.</p>}
            <div className="mt-5 space-y-4">
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Title
                <input disabled={!canEdit} required value={pTitle} onChange={e => setPTitle(e.target.value)} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Executive summary (1-2 lines)
                <input disabled={!canEdit} required value={pSummary} onChange={e => setPSummary(e.target.value)} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Full pitch text (problem, solution, market, traction, ask)
                <textarea disabled={!canEdit} required rows={8} value={pFull} onChange={e => setPFull(e.target.value)} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <button disabled={!canEdit || psBusy} type="submit" data-testid={STARTUP.pitchSubmit} className="ve-btn-primary">
                {psBusy ? 'Categorising…' : 'Submit & categorise'}
              </button>
            </div>
          </form>
        )}

        {tab === 'qa' && (
          <div className="ve-card p-6 max-w-3xl">
            <h2 className="display text-xl">AI Due-Diligence Interview</h2>
            <p className="text-sm text-slate-600 mt-2">A 6-question, hard-edge interview to stress-test traction, defensibility, economics, team and risks.</p>
            {!qaSession && (
              <button onClick={startQA} disabled={qaBusy} data-testid={STARTUP.qaStart} className="mt-5 ve-btn-primary">{qaBusy ? 'Starting…' : 'Start interview'}</button>
            )}
            {qaSession && (
              <div className="mt-6">
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-2">
                  {qaSession.messages.map((m, i) => (
                    <div key={`qa-${i}-${m.role}`} className={`p-3 rounded-md ${m.role === 'ai' ? 'bg-slate-50 border border-slate-200' : 'bg-[#8B3FC5]/5 border border-[#8B3FC5]/20 ml-8'}`}>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">{m.role === 'ai' ? 'Vigilant AI' : 'Founder'}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                    </div>
                  ))}
                </div>
                {!qaSession.completed && canEdit && (
                  <div className="mt-4 flex gap-2">
                    <textarea rows={2} value={qaAnswer} onChange={e => setQaAnswer(e.target.value)} placeholder="Your answer…" className="flex-1 rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
                    <button onClick={sendAnswer} disabled={qaBusy} data-testid={STARTUP.qaSend} className="ve-btn-primary shrink-0"><Send size={14}/></button>
                  </div>
                )}
                {qaSession.completed && (
                  <div className="mt-5 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                    <div className="font-medium text-emerald-900">Interview complete · {qaSession.final_verdict}</div>
                    <div className="mt-1 text-emerald-800">Final score: <span className="display text-lg">{qaSession.final_score}</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'psycho' && (
          <form onSubmit={submitPsycho} className="ve-card p-6 max-w-3xl" data-testid="psycho-intake-form">
            <h2 className="display text-xl">Founder Psychometric (OCEAN)</h2>
            <p className="text-sm text-slate-600 dark:text-white/65 mt-2">
              Twelve quick statements. Rate each on a 1–5 Likert scale. Claude Sonnet
              produces a Big-Five OCEAN profile, leadership index and risk appetite from
              your answers — usually under 8 seconds end-to-end.
            </p>

            <div className="mt-6 space-y-4">
              {PSYCHO_QUESTIONS.map((q, i) => {
                const answered = !!answers[q];
                return (
                  <div key={q}
                    data-testid={`psycho-q-${i}`}
                    className={`rounded-lg border p-4 transition-colors ${
                      answered
                        ? 'border-[#8B3FC5]/40 bg-[#FFF8E5]/40 dark:bg-[#8B3FC5]/[0.04]'
                        : 'border-slate-200 dark:border-white/10'
                    }`}>
                    <div className="text-sm font-medium text-slate-800 dark:text-white/85 leading-snug">
                      <span className="text-[#5E1F8B] dark:text-[#8B3FC5] mr-1.5">{i + 1}.</span>{q}
                    </div>
                    <div className="mt-3 grid grid-cols-5 gap-1.5">
                      {LIKERT_LABELS.map((label, idx) => {
                        const val = String(idx + 1);
                        const selected = answers[q] === val;
                        return (
                          <button
                            key={label}
                            type="button"
                            disabled={!canEdit}
                            onClick={() => setAnswers({ ...answers, [q]: val })}
                            data-testid={`psycho-q-${i}-opt-${idx + 1}`}
                            className={`px-2 py-2 rounded-md text-[10px] uppercase tracking-[0.12em] font-bold border transition-all
                              ${selected
                                ? 'bg-[#8B3FC5] text-[#0A0A0A] border-[#8B3FC5] shadow-sm'
                                : 'bg-white dark:bg-white/[0.03] text-slate-600 dark:text-white/65 border-slate-200 dark:border-white/10 hover:border-[#8B3FC5]'}
                              disabled:opacity-50 disabled:cursor-not-allowed`}>
                            <div className="text-sm font-bold leading-none">{idx + 1}</div>
                            <div className="text-[8.5px] mt-1 opacity-80 leading-tight">{label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55">
                {Object.keys(answers).length} of {PSYCHO_QUESTIONS.length} answered
              </div>
              <button
                disabled={!canEdit || psyBusy || Object.keys(answers).length < PSYCHO_QUESTIONS.length}
                type="submit"
                data-testid={STARTUP.psychoSubmit}
                className="ve-btn-primary">
                {psyBusy ? 'Analysing…' : 'Submit for AI analysis'}
              </button>
            </div>
            {!canEdit && <p className="mt-3 text-xs text-amber-700">Only the founder or admin can submit.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
